import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import chinaRegions from "./chinaRegions.json";
import { apiFetch, apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";

const emptyForm = {
  name: "",
  phone: "",
  wechat: "",
  email: "",
  province: "",
  city: "",
  role: "leader",
  joined_online: 0,
  online_months: "",
  experience: "",
  motivation: "",
  time_commitment: "",
  expected_size: "",
  venue_note: "",
  partners_note: "",
  extra: "",
};

export default function CityBookClubApplyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = Number(localStorage.getItem("user_id") || 0);

  const [roles, setRoles] = useState([]);
  const [hint, setHint] = useState("收到报名后，我们会尽快完成合作评估并与您联系。");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [mine, setMine] = useState([]);
  const [error, setError] = useState("");

  useSeo({
    title: "线下城市读书会合作招募 - AI婚恋平台",
    metaDescription:
      "申请在本市发起线下读书会：与线上共读同一本书，先学习成长，再建立关系。登录会员可填写申请表。",
    path: "/book-club/city-apply",
  });

  useEffect(() => {
    if (!userId) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [userId, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!userId) return;

    apiFetch("/api/public/book-club/city-apply/meta")
      .then((res) => res.json())
      .then((data) => {
        setRoles(data.roles || []);
        if (data.message) setHint(data.message);
      })
      .catch(() => {});

    apiFetch(`/api/profile/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data || data.detail) return;
        setForm((prev) => ({
          ...prev,
          name: data.name || prev.name,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          province: data.province || prev.province,
          city: data.city || prev.city,
        }));
      })
      .catch(() => {});

    apiFetch(`/api/book-club/city-apply/mine?user_id=${userId}`)
      .then((res) => res.json())
      .then((data) => setMine(data.items || []))
      .catch(() => {});
  }, [userId]);

  const cities = useMemo(() => {
    const province = chinaRegions.find((item) => item.name === form.province);
    return province?.children || [];
  }, [form.province]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      navigate("/login");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/book-club/city-apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, user_id: userId, joined_online: form.joined_online ? 1 : 0 }),
      });
      if (!res.ok) {
        setError(data?.detail || "提交失败，请稍后重试");
        return;
      }
      setDone(true);
      setMine((prev) => [data.item, ...prev]);
    } catch (err) {
      setError(err.message || "提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-gray-400">跳转登录中...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-10">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="text-sm text-gray-500">
            <Link to="/book-club" className="text-pink-600 hover:underline">
              读书会
            </Link>
            <span className="mx-2">/</span>
            <span>城市招募申请</span>
          </div>

          <header className="bg-white rounded-2xl shadow border border-pink-100 p-6 md:p-8 space-y-4">
            <p className="text-sm text-pink-600 font-medium">线下城市读书会 · 合作招募</p>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">申请发起本市线下读书会</h1>
            <p className="text-gray-600 leading-relaxed">
              与线上共读同一本书，在你的城市组织轻量、稳定的线下相聚。我们希望先一起学习成长，再慢慢认识、建立关系。
            </p>
            <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
              <li>书目与线上读书会保持一致</li>
              <li>负责人 / 同工从深度参与者中产生</li>
              <li>收到申请后我们会尽快评估并联系你</li>
            </ul>
          </header>

          {mine.length > 0 && (
            <section className="bg-white rounded-2xl border border-amber-100 p-5 space-y-3">
              <h2 className="font-semibold text-gray-800">我已提交的申请</h2>
              {mine.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-50 pb-2 last:border-0"
                >
                  <span>
                    {item.province}
                    {item.city} · {item.role_label}
                  </span>
                  <span className="text-pink-600">{item.status_label}</span>
                </div>
              ))}
            </section>
          )}

          {done ? (
            <section className="bg-white rounded-2xl shadow p-8 text-center space-y-4">
              <h2 className="text-xl font-bold text-gray-800">申请已提交</h2>
              <p className="text-gray-600">{hint}</p>
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                <Link
                  to="/book-club"
                  className="px-5 py-2.5 rounded-full bg-pink-500 text-white text-sm font-medium"
                >
                  返回读书会
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setDone(false);
                    setForm((prev) => ({
                      ...emptyForm,
                      name: prev.name,
                      phone: prev.phone,
                      email: prev.email,
                    }));
                  }}
                  className="px-5 py-2.5 rounded-full border border-pink-200 text-pink-600 text-sm font-medium"
                >
                  再申请其他城市
                </button>
              </div>
            </section>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow border border-pink-100 p-6 md:p-8 space-y-6">
              <h2 className="font-bold text-gray-800 text-lg">填写申请表</h2>
              <p className="text-sm text-gray-500">{hint}</p>

              {error && (
                <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl">{error}</div>
              )}

              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-pink-600">基础信息</legend>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="称呼 *" value={form.name} onChange={(v) => update({ name: v })} required />
                  <Field label="手机号 *" value={form.phone} onChange={(v) => update({ phone: v })} required />
                  <Field label="微信号 *" value={form.wechat} onChange={(v) => update({ wechat: v })} required />
                  <Field label="邮箱" value={form.email} onChange={(v) => update({ email: v })} />
                </div>
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-pink-600">城市与角色</legend>
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block text-sm">
                    <span className="text-gray-600">省份 *</span>
                    <select
                      value={form.province}
                      onChange={(e) => update({ province: e.target.value, city: "" })}
                      className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                      required
                    >
                      <option value="">请选择</option>
                      {chinaRegions.map((p) => (
                        <option key={p.code} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-600">城市 *</span>
                    <select
                      value={form.city}
                      onChange={(e) => update({ city: e.target.value })}
                      className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                      required
                      disabled={!form.province}
                    >
                      <option value="">请选择</option>
                      {cities.map((c) => (
                        <option key={c.code} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="text-gray-600">意向角色 *</span>
                  <select
                    value={form.role}
                    onChange={(e) => update({ role: e.target.value })}
                    className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                  >
                    {(roles.length
                      ? roles
                      : [
                          { value: "leader", label: "负责人" },
                          { value: "co_worker", label: "同工协助" },
                          { value: "participant_first", label: "先深度参与再考虑负责" },
                        ]
                    ).map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-pink-600">背景与动机</legend>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.joined_online}
                    onChange={(e) => update({ joined_online: e.target.checked ? 1 : 0 })}
                  />
                  我参加过线上读书会
                </label>
                <Field
                  label="参加线上多久（选填）"
                  value={form.online_months}
                  onChange={(v) => update({ online_months: v })}
                  placeholder="例如：约 3 个月"
                />
                <Area
                  label="过往组织 / 服事经验（选填）"
                  value={form.experience}
                  onChange={(v) => update({ experience: v })}
                />
                <Area
                  label="为什么想在本市发起？*"
                  value={form.motivation}
                  onChange={(v) => update({ motivation: v })}
                  required
                />
                <Field
                  label="可投入时间"
                  value={form.time_commitment}
                  onChange={(v) => update({ time_commitment: v })}
                  placeholder="例如：每月半天"
                />
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-pink-600">落地情况（选填）</legend>
                <Field
                  label="第一次大概人数"
                  value={form.expected_size}
                  onChange={(v) => update({ expected_size: v })}
                  placeholder="例如：8～15 人"
                />
                <Area
                  label="场地条件"
                  value={form.venue_note}
                  onChange={(v) => update({ venue_note: v })}
                  placeholder="已有 / 可协调 / 需要建议"
                />
                <Area
                  label="是否已有同工伙伴"
                  value={form.partners_note}
                  onChange={(v) => update({ partners_note: v })}
                />
                <Area label="其他说明" value={form.extra} onChange={(v) => update({ extra: v })} />
              </fieldset>

              <label className="flex items-start gap-2 text-sm text-gray-600">
                <input type="checkbox" required className="mt-1" />
                <span>
                  我认同「先学习成长，再建立关系」的理念，愿意与线上共读同一本书，并同意平台与我联系进行评估。
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 rounded-full bg-pink-500 text-white font-semibold hover:bg-pink-600 disabled:opacity-60"
              >
                {submitting ? "提交中..." : "提交申请"}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "", required = false }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-600">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
      />
    </label>
  );
}

function Area({ label, value, onChange, placeholder = "", required = false }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-600">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={3}
        className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5 resize-y"
      />
    </label>
  );
}
